// src/components/leaves/LeaveForm.js
import React, { useState, useEffect } from 'react';

const LeaveForm = ({ leaveTypes, premises, onSubmit, onClose, user }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        staff_id: '',
        premises_id: '',
        leave_type_id: '',
        start_date: '',
        end_date: '',
        reason: '',
        attachment: null,
        emergency_contact: '',
        emergency_phone: '',
        half_day: false,
        half_day_period: 'morning' // morning or afternoon
    });

    const [calculatedDays, setCalculatedDays] = useState(0);
    const [dateError, setDateError] = useState('');

    const isStaff = user?.user_type === 'staff';

    useEffect(() => {
        // Pre-fill staff_id and premises_id for staff users
        if (isStaff && user?.staff_id) {
            setFormData(prev => ({
                ...prev,
                staff_id: user.staff_id,
                premises_id: user.premises_id || ''
            }));
        }
    }, [user, isStaff]);

    useEffect(() => {
        calculateLeaveDays();
    }, [formData.start_date, formData.end_date, formData.half_day]);

    const calculateLeaveDays = () => {
        if (formData.start_date && formData.end_date) {
            const start = new Date(formData.start_date);
            const end = new Date(formData.end_date);

            if (end < start) {
                setDateError('End date cannot be before start date');
                setCalculatedDays(0);
                return;
            }

            setDateError('');

            // Calculate business days (excluding weekends)
            let count = 0;
            let current = new Date(start);

            while (current <= end) {
                const day = current.getDay();
                if (day !== 0 && day !== 6) { // Not Sunday (0) or Saturday (6)
                    count++;
                }
                current.setDate(current.getDate() + 1);
            }

            // If it's a half day and single day, count as 0.5
            if (formData.half_day && start.getTime() === end.getTime()) {
                count = 0.5;
            }

            setCalculatedDays(count);
        } else {
            setCalculatedDays(0);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('File size must be less than 5MB');
                return;
            }

            // Check file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                setError('Only JPEG, PNG, PDF, DOC, and DOCX files are allowed');
                return;
            }

            setFormData(prev => ({
                ...prev,
                attachment: file
            }));
        }
    };

    const validateForm = () => {
        const errors = [];

        if (!formData.staff_id) {
            errors.push('Staff selection is required');
        }

        if (!formData.premises_id) {
            errors.push('Premises selection is required');
        }

        if (!formData.leave_type_id) {
            errors.push('Leave type is required');
        }

        if (!formData.start_date) {
            errors.push('Start date is required');
        }

        if (!formData.end_date) {
            errors.push('End date is required');
        }

        if (!formData.reason.trim()) {
            errors.push('Leave reason is required');
        }

        if (formData.reason.trim().length < 10) {
            errors.push('Leave reason must be at least 10 characters');
        }

        if (dateError) {
            errors.push(dateError);
        }

        if (calculatedDays <= 0) {
            errors.push('Leave duration must be at least 0.5 days');
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setError(validationErrors.join(', '));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const submitData = new FormData();

            // Append form fields
            Object.keys(formData).forEach(key => {
                if (key === 'attachment' && formData[key]) {
                    submitData.append('attachment', formData[key]);
                } else if (key !== 'attachment') {
                    submitData.append(key, formData[key]);
                }
            });

            // Add calculated total days
            submitData.append('total_days', calculatedDays);

            await onSubmit(submitData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getLeaveTypeDetails = (leaveTypeId) => {
        return leaveTypes.find(type => type.id === parseInt(leaveTypeId));
    };

    const selectedLeaveType = getLeaveTypeDetails(formData.leave_type_id);

    return (
        <div className="modal-overlay">
            <div className="modal leave-form-modal">
                <div className="modal-header">
                    <h3>Apply for Leave</h3>
                    <button className="close-btn" onClick={onClose} type="button">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-body">
                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            {error}
                        </div>
                    )}

                    <div className="form-sections">
                        {/* Basic Information */}
                        <div className="form-section">
                            <h4>
                                <i className="fas fa-info-circle"></i>
                                Leave Details
                            </h4>

                            <div className="form-grid">
                                {!isStaff && (
                                    <div className="form-group">
                                        <label>Staff Member *</label>
                                        <select
                                            className="form-input"
                                            value={formData.staff_id}
                                            onChange={(e) => handleInputChange('staff_id', e.target.value)}
                                            required
                                        >
                                            <option value="">Select Staff Member</option>
                                            {/* This would be populated with staff list in a real implementation */}
                                        </select>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Premises *</label>
                                    <select
                                        className="form-input"
                                        value={formData.premises_id}
                                        onChange={(e) => handleInputChange('premises_id', e.target.value)}
                                        required
                                        disabled={isStaff && user?.premises_id}
                                    >
                                        <option value="">Select Premises</option>
                                        {premises.map(premise => (
                                            <option key={premise.id} value={premise.id}>
                                                {premise.premises_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Leave Type *</label>
                                    <select
                                        className="form-input"
                                        value={formData.leave_type_id}
                                        onChange={(e) => handleInputChange('leave_type_id', e.target.value)}
                                        required
                                    >
                                        <option value="">Select Leave Type</option>
                                        {leaveTypes.map(type => (
                                            <option key={type.id} value={type.id}>
                                                {type.leave_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedLeaveType && (
                                    <div className="leave-type-info">
                                        <div className="info-card">
                                            <h5>{selectedLeaveType.leave_name}</h5>
                                            <div className="info-details">
                                                {selectedLeaveType.max_days && (
                                                    <p><strong>Maximum Days:</strong> {selectedLeaveType.max_days}</p>
                                                )}
                                                <p><strong>Requires Approval:</strong> {selectedLeaveType.requires_approval ? 'Yes' : 'No'}</p>
                                                {selectedLeaveType.description && (
                                                    <p><strong>Description:</strong> {selectedLeaveType.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Date Selection */}
                        <div className="form-section">
                            <h4>
                                <i className="fas fa-calendar-alt"></i>
                                Leave Period
                            </h4>

                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Start Date *</label>
                                    <input
                                        type="date"
                                        className="form-input"
                                        value={formData.start_date}
                                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>End Date *</label>
                                    <input
                                        type="date"
                                        className="form-input"
                                        value={formData.end_date}
                                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                                        min={formData.start_date || new Date().toISOString().split('T')[0]}
                                        required
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.half_day}
                                            onChange={(e) => handleInputChange('half_day', e.target.checked)}
                                        />
                                        Half Day Leave
                                    </label>
                                    <p className="form-help">
                                        Check this if you're applying for a half day leave (only applicable for single day)
                                    </p>
                                </div>

                                {formData.half_day && (
                                    <div className="form-group">
                                        <label>Half Day Period</label>
                                        <select
                                            className="form-input"
                                            value={formData.half_day_period}
                                            onChange={(e) => handleInputChange('half_day_period', e.target.value)}
                                        >
                                            <option value="morning">Morning (First Half)</option>
                                            <option value="afternoon">Afternoon (Second Half)</option>
                                        </select>
                                    </div>
                                )}

                                {calculatedDays > 0 && (
                                    <div className="leave-summary">
                                        <div className="summary-card">
                                            <h5>Leave Summary</h5>
                                            <div className="summary-details">
                                                <p><strong>Total Days:</strong> {calculatedDays} {calculatedDays === 1 ? 'day' : 'days'}</p>
                                                <p><strong>Leave Type:</strong> {selectedLeaveType?.leave_name}</p>
                                                <p><strong>Period:</strong> {formData.start_date} to {formData.end_date}</p>
                                                {formData.half_day && (
                                                    <p><strong>Half Day Period:</strong> {formData.half_day_period}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Leave Details */}
                        <div className="form-section">
                            <h4>
                                <i className="fas fa-edit"></i>
                                Leave Information
                            </h4>

                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Reason for Leave *</label>
                                    <textarea
                                        className="form-input"
                                        value={formData.reason}
                                        onChange={(e) => handleInputChange('reason', e.target.value)}
                                        placeholder="Please provide a detailed reason for your leave request..."
                                        rows="4"
                                        required
                                    />
                                    <p className="form-help">
                                        Minimum 10 characters. Be specific about the reason for your leave.
                                    </p>
                                </div>

                                <div className="form-group">
                                    <label>Emergency Contact Name</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.emergency_contact}
                                        onChange={(e) => handleInputChange('emergency_contact', e.target.value)}
                                        placeholder="Contact person during leave"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Emergency Contact Phone</label>
                                    <input
                                        type="tel"
                                        className="form-input"
                                        value={formData.emergency_phone}
                                        onChange={(e) => handleInputChange('emergency_phone', e.target.value)}
                                        placeholder="Emergency phone number"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Supporting Document</label>
                                    <input
                                        type="file"
                                        className="form-input"
                                        onChange={handleFileChange}
                                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                    />
                                    <p className="form-help">
                                        Upload medical certificate or other supporting documents (Max 5MB, JPEG/PNG/PDF/DOC/DOCX)
                                    </p>
                                    {formData.attachment && (
                                        <div className="file-preview">
                                            <div className="file-info">
                                                <i className="fas fa-file"></i>
                                                <span>{formData.attachment.name}</span>
                                                <button
                                                    type="button"
                                                    className="remove-file"
                                                    onClick={() => handleInputChange('attachment', null)}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading || calculatedDays <= 0}>
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-paper-plane"></i>
                                    Submit Leave Request
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: var(--spacing-md);
                }

                .leave-form-modal {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-lg);
                    width: 100%;
                    max-width: 800px;
                    max-height: 95vh;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-xl);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                }

                .modal-header h3 {
                    margin: 0;
                    color: var(--text-dark);
                    font-size: 1.3rem;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    color: var(--text-light);
                    padding: var(--spacing-xs);
                    border-radius: var(--radius-sm);
                    transition: all 0.2s ease;
                }

                .close-btn:hover {
                    color: var(--text-dark);
                    background: rgba(0, 0, 0, 0.05);
                }

                .modal-body {
                    padding: var(--spacing-xl);
                    overflow-y: auto;
                    flex: 1;
                }

                .error-message {
                    background: rgba(239, 68, 68, 0.1);
                    color: var(--error);
                    padding: var(--spacing-md);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--spacing-lg);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    font-size: 0.9rem;
                }

                .form-sections {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xl);
                }

                .form-section {
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    border-radius: var(--radius-lg);
                    overflow: hidden;
                }

                .form-section h4 {
                    background: var(--light-color);
                    color: var(--text-dark);
                    margin: 0;
                    padding: var(--spacing-lg);
                    font-size: 1.1rem;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                }

                .form-section h4 i {
                    color: var(--admin-primary);
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: var(--spacing-lg);
                    padding: var(--spacing-lg);
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-sm);
                }

                .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .form-group label {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .form-input {
                    padding: var(--spacing-md);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                    background: var(--white);
                }

                .form-input:focus {
                    outline: none;
                    border-color: var(--admin-primary);
                    box-shadow: 0 0 0 3px rgba(var(--admin-primary-rgb), 0.1);
                }

                .form-input:disabled {
                    background: var(--light-color);
                    color: var(--text-light);
                    cursor: not-allowed;
                }

                .form-help {
                    font-size: 0.8rem;
                    color: var(--text-light);
                    margin: var(--spacing-xs) 0 0 0;
                    line-height: 1.4;
                }

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    font-size: 0.9rem;
                    cursor: pointer;
                    padding: var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    transition: background-color 0.2s ease;
                }

                .checkbox-label:hover {
                    background: rgba(var(--admin-primary-rgb), 0.05);
                }

                .checkbox-label input[type="checkbox"] {
                    accent-color: var(--admin-primary);
                    margin: 0;
                }

                .leave-type-info,
                .leave-summary {
                    grid-column: 1 / -1;
                }

                .info-card,
                .summary-card {
                    background: var(--light-color);
                    border-radius: var(--radius-md);
                    padding: var(--spacing-lg);
                    border: 1px solid rgba(0, 0, 0, 0.05);
                }

                .info-card h5,
                .summary-card h5 {
                    margin: 0 0 var(--spacing-md) 0;
                    color: var(--text-dark);
                    font-size: 1rem;
                }

                .info-details p,
                .summary-details p {
                    margin: var(--spacing-xs) 0;
                    font-size: 0.85rem;
                    color: var(--text-dark);
                }

                .file-preview {
                    margin-top: var(--spacing-sm);
                }

                .file-info {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    padding: var(--spacing-sm);
                    background: var(--light-color);
                    border-radius: var(--radius-md);
                    font-size: 0.85rem;
                }

                .file-info i {
                    color: var(--admin-primary);
                }

                .remove-file {
                    background: var(--error);
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 20px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 0.7rem;
                    margin-left: auto;
                }

                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: var(--spacing-sm);
                    padding: var(--spacing-xl);
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                }

                .btn {
                    padding: var(--spacing-sm) var(--spacing-lg);
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-sm);
                    min-width: 140px;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background: var(--admin-primary);
                    color: white;
                }

                .btn-primary:hover:not(:disabled) {
                    background: var(--admin-secondary);
                    transform: translateY(-1px);
                }

                .btn-secondary {
                    background: var(--white);
                    color: var(--text-dark);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f8f9fa;
                    border-color: var(--admin-primary);
                }

                .fa-spinner {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .modal-overlay {
                        padding: var(--spacing-sm);
                    }

                    .leave-form-modal {
                        max-width: 100%;
                        max-height: 100vh;
                    }

                    .modal-header,
                    .modal-body,
                    .modal-actions {
                        padding: var(--spacing-lg);
                    }

                    .form-grid {
                        grid-template-columns: 1fr;
                        padding: var(--spacing-md);
                    }

                    .modal-actions {
                        flex-direction: column;
                    }

                    .btn {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default LeaveForm;